import * as Sentry from '@sentry/react'

const isErrorFromAppCode = (event: Sentry.ErrorEvent): boolean => {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames
  if (!frames?.length) return true

  return frames.some(
    (frame) =>
      frame.filename &&
      (frame.filename.includes('/assets/') ||
        frame.filename.includes('localhost') ||
        frame.filename.includes('.fuze-app.ca') ||
        frame.filename.includes('.v7apps.com')),
  )
}

const hasClientErrorStatus = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) return false
  if (!('status' in value)) return false
  const status = (value as { status: unknown }).status
  return typeof status === 'number' && status >= 400 && status < 500
}

const SentryConfig = (
  dataSourceName: string,
  environment: string = 'development',
  release?: string,
) => ({
  dsn: dataSourceName,
  environment,
  release,

  autoSessionTracking: true,

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  // Filter out errors from browser extensions and third-party scripts, and drop
  // 4xx client errors (expected application state — auth, permissions, validation,
  // not-found — not bugs worth alerting on)
  beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
    if (!isErrorFromAppCode(event)) return null
    if (hasClientErrorStatus(hint?.originalException)) return null
    return event
  },

  integrations: [
    // If you're using react router, use the integration for your react router version instead.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // NOTE: This will disable built-in masking.
      // Only use this if your site has no sensitive data, or if you've already set up other options for masking or blocking relevant data, such as 'ignore', 'block', 'mask' and 'maskFn'.
      maskAllText: false,
      blockAllMedia: false,
    }),
    Sentry.feedbackIntegration({
      autoInject: false,
      isNameRequired: true,
      isEmailRequired: true,
    }),
    Sentry.consoleLoggingIntegration({ levels: ['error', 'warn'] }),
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
  ],

  // Enable logs to be sent to Sentry,
  enableLogs: true,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.25,

  tracePropagationTargets: [
    'localhost', // For local development
    'https://fuze-dev-api.v7apps.com',
    'https://fuze-qa-api.v7apps.com',
    'https://fuze-preprod-api.v7apps.com',
    'https://fuze-app.ca',
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 0.25,
})

export const initSentry = (
  dataSourceName: string | undefined,
  environment: string,
  release?: string,
) => {
  if (!dataSourceName) return
  Sentry.init(SentryConfig(dataSourceName, environment, release))
}

// Export Sentry instance to be used throughout the app (e.g. Sentry.captureMessage)
export { Sentry }
